import React, { useContext, useState, useEffect, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import 'dayjs/locale/ja'
import 'react-dates/initialize'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { useCheckinUpdatedListener } from '@/hooks/use-checkin-updated-listener'
import { useScrollToTop } from '@/hooks/use-scroll-to-top'
import { AccountContext } from '@/contexts/account'
import { ensureCsvExportPermissionOrAlert } from '@/libs/hotel-pms-permission'
import { wholeContainerStyle, mainColumnStyle, mainContainerStyle, listContainerGrayStyle } from '@/constants/layout'
import { MAX_NUMBER_DOWNLOADS } from '@/constants/accommodation-management'
import * as api from '@/apis/aipass'
import { LoadingFull } from '@/components/molecules/loading-full'
import { Header } from '@/components/organisms/header'
import { SideMenu } from '@/components/organisms/side-menu'
import { CheckinList } from '@/components/pages/accommodation-management/_checkin/list'
import { StayList } from '@/components/pages/accommodation-management/_stay/list'
import { CheckoutList } from '@/components/pages/accommodation-management/_checkout/list'
import { ReservationList } from '@/components/pages/accommodation-management/_reservation/list'
import { Button } from '@/components/atoms/button'
import { CsvUploadModal } from '@/components/pages/accommodation-management/csv-upload-modal'
import { CSVReader } from '@/components/organisms/csv-reader'
import { hasReservationTaskPlugin, setHasTicketPlugin } from '@/libs/plugins'
import { CountBadge } from '@/components/molecules/count-badge'
import { ReservationTaskTab } from '@/components/organisms/accommodation-management/reservation-task/reservation-task-tab'
import { RESERVATION_TASK_TAB_TYPE } from '@/models/reservation/reservation-task'
import { isUnavailableReservationTaskTab } from '@/utils/reservation-task'
import { Popup } from '@/components/atoms/popup'
import { EntireSearchContentInitial, EntireSearchContentType } from '@/models/entire-search'
import qs from 'qs'
import { SortKeyType } from '@/components/molecules/sortable-table-header'
import { SearchReservationList } from '@/models/reservation'

type PageParams = {
  refresh?: number
  page: number
  tabType: TabType
  sortKey: SortKeyType
} & EntireSearchContentType

const TabType = {
  Reservation: 'reservation',
  Checkin: 'checkin',
  Stay: 'stay',
  Checkout: 'checkout',
  ReservationTask: RESERVATION_TASK_TAB_TYPE,
} as const
export type TabType = (typeof TabType)[keyof typeof TabType]

export const SortKey = {
  CheckinId: 'CheckinId',
  ReservationId: 'ReservationId',
  GuestName: 'GuestName',
  ReserveGuestName: 'ReserveGuestName',
  ReserveUserName: 'ReserveUserName',
  ReserveStatus: 'ReserveStatus',
  ApprovedStatus: 'ApprovedStatus',
  RoomType: 'RoomType',
  RoomNumber: 'RoomNumber',
  CheckinDate: 'CheckinDate',
  CheckoutDate: 'CheckoutDate',
  ArrivalTime: 'ArrivalTime',
  Evaluation: 'Evaluation',
  UpdatedAt: 'UpdatedAt',
} as const
export type SortKey = (typeof SortKey)[keyof typeof SortKey]

export const AccommodationManagement: React.FC = () => {
  const { t } = useTranslation()
  const { plugins, account, permissions, permissionsLoaded, permissionsError } = useContext<any>(AccountContext)
  const hasTicketPlugin = setHasTicketPlugin(plugins)
  const hasReservationTask = hasReservationTaskPlugin(plugins)
  const { errorHandler } = useErrorHandler()
  const history = useHistory()
  const params: PageParams = qs.parse(window.location.search.substring(1))
  const [listTotal, setListTotal] = useState<number>(0)
  const [checkinList, setCheckinList] = useState([])
  const [reservationList, setReservationList] = useState<SearchReservationList[]>([])
  const [hasList, setHaslist] = useState(0)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [pagingIsDisabled, setPagingIsDisabled] = useState<boolean>(true)
  const [isDropDownOpen, setIsDropDownOpen] = useState<boolean>(false)
  const [isStatusApproved, setIsStatusApproved] = useState<boolean>(false)
  const [isCsvUploadModalOpen, setIsCsvUploadModalOpen] = useState<boolean>(false)
  const [tabType, setTabType] = useState<TabType>(params.tabType || TabType.Checkin)
  const [page, setPage] = useState<number>(params.page ? Number(params.page) : 1)
  const [unhandledTaskTotal, setUnhandledTaskTotal] = useState<number>(0)
  // タスク管理タブの見出しを押すたびに増やし、ReservationTaskTab を作り直して既定の条件で開く（onChangeTab）
  const [reservationTaskTabKey, setReservationTaskTabKey] = useState<number>(0)
  const getDefaultSortKeyByTab = (_tabType: TabType): SortKeyType => {
    switch (_tabType) {
      case TabType.Checkin:
        return { key: SortKey.ArrivalTime, order: 'asc' }
      case TabType.Stay:
        return { key: SortKey.UpdatedAt, order: 'desc' }
      case TabType.Checkout:
        return { key: SortKey.UpdatedAt, order: 'desc' }
      default:
        return { key: SortKey.CheckinDate, order: 'asc' }
    }
  }
  const [sortKey, setSortKey] = useState<SortKeyType>(params.sortKey || getDefaultSortKeyByTab(tabType))
  const { scrollRef: listScrollRef, scrollToTop: scrollListToTop } = useScrollToTop()
  // タブの切り替えと更新後の取り直しで取得が重なると、遅れて届いた別のタブの結果で一覧が上書きされる。
  // 予約・滞在中・チェックアウトは同じ一覧を使うため、開いているタブと違うステータスの予約が並び、そこから一括変更されてしまう
  const latestListRequestIdRef = useRef<number>(0)

  const perPageNumber = 50
  const defaultSearchDateRange = (_tabType: TabType) => {
    switch (_tabType) {
      case 'reservation':
        return {
          checkinDateRange: {
            from: dayjs().format('YYYY-MM-DD'),
            to: dayjs().format('YYYY-MM-DD'),
          },
        }
      case 'checkin':
        return {
          checkinDateRange: {
            from: dayjs().format('YYYY-MM-DD'),
            to: dayjs().add(1, 'day').format('YYYY-MM-DD'),
          },
        }
      case 'checkout':
        return {
          checkoutDateRange: {
            from: dayjs().format('YYYY-MM-DD'),
            to: dayjs().format('YYYY-MM-DD'),
          },
        }
      default:
        return {}
    }
  }
  const [detailedResearch, setDetailedResearch] = useState<EntireSearchContentType>({
    ...EntireSearchContentInitial({ tabType, override: defaultSearchDateRange(tabType) }),
  })

  const fetchAdminCheckinList = async (opt: {
    detailedResearch: EntireSearchContentType
    tabType: TabType
    page: number | string
    sortKey: SortKeyType
  }) => {
    if (!account) {
      return
    }
    if (opt.tabType === TabType.ReservationTask) {
      // タスク管理タブは一覧・条件・URL を ReservationTaskTab が持つ。ここで取得を続けると予約一覧の API を呼び、
      // URL も予約一覧の条件で上書きしてしまう。初回表示・タブ切り替え・戻る・チェックイン更新の通知がすべてここを通る
      setTabType(opt.tabType)
      // 一括変更の送信中に戻る操作でこのタブへ移ると、送信後の取り直しがここで止まり、送信時に出したローダーを消す処理が無くなる。
      // 届いていない他のタブの取得結果も、このタブの表示中に一覧やローダーを書き換えないよう捨てる
      latestListRequestIdRef.current++
      setIsLoading(false)
      return
    }

    const requestId = ++latestListRequestIdRef.current
    setIsLoading(true)
    setPagingIsDisabled(true)
    setPage(Number(opt.page))
    setTabType(opt.tabType)
    setSortKey(opt.sortKey)
    setDetailedResearch(opt.detailedResearch)

    history.replace({
      pathname: '',
      search: qs.stringify({ ...opt.detailedResearch, page: opt.page, tabType: opt.tabType, sortKey: opt.sortKey }),
    })
    const isLatestRequest = () => requestId === latestListRequestIdRef.current
    let res: { list: Array<any>; total: number }
    if (opt.tabType !== TabType.Checkin) {
      res = await api
        .fetchReservation({
          ...opt.detailedResearch,
          page: Number(opt.page),
          limit: perPageNumber,
          sortKey: opt.sortKey,
        })
        .then(result => {
          const list = result?.reservations || []
          if (isLatestRequest()) {
            setReservationList(list)
            scrollListToTop()
          }
          return { list, total: result?.total || 0 }
        })
    } else {
      res = await api
        .fetchAdminCheckinList(
          Number(opt.page),
          perPageNumber,
          opt.detailedResearch.name,
          opt.detailedResearch.nameKana,
          opt.detailedResearch.searchReservationId,
          opt.detailedResearch.roomNumber,
          opt.detailedResearch.checkinDateRange?.from,
          opt.detailedResearch.checkinDateRange?.to,
          opt.sortKey,
        )
        .then(result => {
          const list = result?.checkinList || []
          if (isLatestRequest()) {
            setCheckinList(list)
            scrollListToTop()
          }
          return { list, total: result?.total || 0 }
        })
    }
    if (!isLatestRequest()) {
      return
    }
    if (!res.list.length && Number(opt.page) !== 1) {
      setPage(1)
    }
    setListTotal(res.total)
    setHaslist(res.list.length)
    setPagingIsDisabled(false)
    setIsLoading(false)
  }

  useCheckinUpdatedListener(() => fetchAdminCheckinList({ tabType, page, detailedResearch, sortKey }))

  const fetchUnhandledTaskTotal = async () => {
    try {
      const result = await api.fetchReservationTaskUnhandledTotal()
      setUnhandledTaskTotal(result.unhandledTotal)
    } catch (error) {
      errorHandler(error)
    }
  }

  useEffect(() => {
    // 宿泊管理画面はすべての施設が開けるため、タスク管理タブを表示する施設だけで件数を取る。
    // plugins は非同期で読み込まれるので、判定の結果が変わったときに取得する。
    // fetchUnhandledTaskTotal は state の setter と errorHandler しか参照しないため依存に入れない
    if (!hasReservationTask) {
      return
    }
    fetchUnhandledTaskTotal()
  }, [hasReservationTask])

  const downloadAccommodationManagementCsv = async () => {
    if (
      !ensureCsvExportPermissionOrAlert(permissions, t, 'customer_csv_export', {
        permissionsLoaded,
        permissionsError,
      })
    ) {
      return
    }
    if (listTotal >= MAX_NUMBER_DOWNLOADS) {
      alert(
        t('You cannot create more than {{num}} items Please press the download button after narrowing down the number of items', {
          num: MAX_NUMBER_DOWNLOADS,
        }),
      )
      return
    }
    try {
      setIsLoading(true)
      const result = await api.fetchAccommodationManagementCsv(detailedResearch)
      // 将来 API が .catch 等で握りつぶして undefined を返す実装になっても、誤って履歴一覧へ遷移しないようにする
      if (result != null) {
        history.push({ pathname: 'csv-download-history/list' })
      }
    } catch (e) {
      errorHandler(e)
    } finally {
      setIsLoading(false)
    }
  }

  const downloadCustomExport = async () => {
    if (
      !ensureCsvExportPermissionOrAlert(permissions, t, 'customer_csv_export', {
        permissionsLoaded,
        permissionsError,
      })
    ) {
      return
    }
    setIsDropDownOpen(false)
    if (listTotal >= MAX_NUMBER_DOWNLOADS) {
      alert(
        t('You cannot create more than {{num}} items Please press the download button after narrowing down the number of items', {
          num: MAX_NUMBER_DOWNLOADS,
        }),
      )
      return
    }
    try {
      setIsLoading(true)
      // 予約出力と同様、遷移前に API でスタッフ確認・エクスポート権限を BE 判定する
      await api.fetchReservationCustomExportSetting()
      history.push('/accommodation-management/custom-export', detailedResearch)
    } catch (e) {
      errorHandler(e)
    } finally {
      setIsLoading(false)
    }
  }

  const onClearSearchParams = () => {
    setDetailedResearch(EntireSearchContentInitial({ tabType, isDisabledParams: true }))
  }

  // 一覧再取得。フィルタ／ソート時は page=1 を渡して先頭へ戻す
  const fetchTabList = (p: number, nextSortKey: SortKeyType = sortKey) =>
    fetchAdminCheckinList({ page: p, detailedResearch, tabType, sortKey: nextSortKey })
  // 一括変更は完了を待ってから一覧を取り直す。送信したときの描画の fetchTabList を呼ぶと、待つ間に切り替えたタブを
  // 送信したときのタブに戻してしまうため、取り直す時点で開いているタブの条件で呼ぶ
  const fetchTabListRef = useRef(fetchTabList)
  fetchTabListRef.current = fetchTabList
  const refetchCurrentTab = () => fetchTabListRef.current(1)

  const onFileLoad = async (
    data: { reservationId: string; exchangeCode: string; ticketName: string }[],
    file: { name: string; size: number; type: string },
  ): Promise<void> => {
    try {
      const fileName = file.name
      const ticketData = data.map(function (value) {
        return {
          reservationId: value[1],
          exchangeCode: value[2],
          ticketName: value[3],
        }
      })
      await api.insertTicketCsv(fileName, ticketData)
    } catch (error) {
      errorHandler(error)
    }
  }

  const onChangeTab = (_tabType: TabType) => {
    history.push({ pathname: '', search: qs.stringify({ tabType: _tabType }) })
    if (_tabType === TabType.ReservationTask) {
      setReservationTaskTabKey(key => key + 1)
    }
    refreshTab(_tabType)
  }

  const refreshTab = (_tabType: TabType) => {
    const newSortKey = getDefaultSortKeyByTab(_tabType)
    setCheckinList([])
    setReservationList([])
    fetchAdminCheckinList({
      detailedResearch: {
        ...EntireSearchContentInitial({
          tabType: _tabType,
          override: defaultSearchDateRange(_tabType),
        }),
      },
      tabType: _tabType,
      page: 1,
      sortKey: newSortKey,
    })
  }

  useEffect(() => {
    // ブラウザバッグによるタブ変更は再描画とならないためstate反映漏れをトリガーとする
    if (tabType === params.tabType || params.refresh) {
      return
    }
    setCheckinList([])
    setReservationList([])
    fetchAdminCheckinList({ ...params, detailedResearch: EntireSearchContentInitial() })
  }, [params.tabType])

  useEffect(() => {
    params.refresh && refreshTab(params.tabType)
  }, [params.refresh])

  useEffect(() => {
    fetchAdminCheckinList({ tabType, page, detailedResearch, sortKey })
  }, [])

  useEffect(() => {
    // URL でタスク管理タブを指定されても、タブを表示しない施設では描画できる一覧が無く空の画面になるため、チェックインタブを開き直す。
    // refreshTab は URL を replace で書き換えるので、戻る操作で空の画面に戻らない。
    // refreshTab は描画のたびに作り直されるが、参照する account は plugins より先に読み込まれ、ここで古い値にならないため依存に入れない
    if (isUnavailableReservationTaskTab(tabType, plugins)) {
      refreshTab(TabType.Checkin)
    }
  }, [plugins, tabType])

  // チェックアウトタブとタスク管理タブには、右上からまとめて変える操作が無い（タスク管理タブは一覧の左上で変える）
  const isStatusChangeDisabled = tabType === TabType.Checkout || tabType === TabType.ReservationTask

  return (
    <div css={wholeContainerStyle}>
      <SideMenu />
      <div className="mainColumn" css={mainColumnStyle}>
        <Header title={t('Accommodation management')} />
        <div className="mainContainer" css={mainContainerStyle}>
          <div style={{ display: 'flex' }}>
            <ul css={tabStyle}>
              <li
                css={tabType === TabType.Reservation ? activeTabStyle : nonActiveTabStyle}
                onClick={() => onChangeTab(TabType.Reservation)}
              >
                <span css={tabCountTextStyle}>{t('Reserve')}</span>
              </li>
              <li css={tabType === TabType.Checkin ? activeTabStyle : nonActiveTabStyle} onClick={() => onChangeTab(TabType.Checkin)}>
                <span css={tabCountTextStyle}>{t('Checkin')}</span>
              </li>
              <li css={tabType === TabType.Stay ? activeTabStyle : nonActiveTabStyle} onClick={() => onChangeTab(TabType.Stay)}>
                <span css={tabCountTextStyle}>{t('During your stay')}</span>
              </li>
              <li css={tabType === TabType.Checkout ? activeTabStyle : nonActiveTabStyle} onClick={() => onChangeTab(TabType.Checkout)}>
                <span css={tabCountTextStyle}>{t('Checkout')}</span>
              </li>
              {hasReservationTask && (
                <li
                  css={tabType === TabType.ReservationTask ? activeTabStyle : nonActiveTabStyle}
                  onClick={() => onChangeTab(TabType.ReservationTask)}
                >
                  <span css={reservationTaskTabTextStyle}>{t('reservationTask.TabName')}</span>
                  <CountBadge count={unhandledTaskTotal} />
                </li>
              )}
            </ul>
            <div css={headerBarRightWrapperStyle}>
              <div>
                <Button
                  buttonType={4}
                  width={82}
                  height={32}
                  fontSize={12}
                  marginRight={8}
                  icon={require('@/static/images/magnifying_glass.svg')}
                  onClick={() => history.push({ pathname: '/entire-search' })}
                >
                  {t('Search')}
                </Button>
              </div>
              <div css={statusChangeButtonStyle}>
                {!isStatusChangeDisabled && (
                  <Button buttonType={3} width={122} height={32} fontSize={12} marginRight={8} onClick={() => setIsStatusApproved(true)}>
                    {t('Status change')}
                  </Button>
                )}
                {isStatusChangeDisabled && (
                  <Button buttonType={11} width={122} height={32} fontSize={12} marginRight={8} onClick={() => {}}>
                    {t('Status change')}
                  </Button>
                )}
              </div>
              {/* CSV の出力・取込は予約一覧の検索条件で動き、タスクの絞り込みとは関係しないためタスク管理タブでは出さない */}
              {tabType !== TabType.ReservationTask && (
                <div css={dropDownButtonStyle}>
                  <Button
                    buttonType={4}
                    width={100}
                    height={32}
                    fontSize={12}
                    paddingLeft={16}
                    paddingRight={16}
                    icon={require('@/static/images/csv.svg')}
                    onClick={() => setIsDropDownOpen(!isDropDownOpen)}
                  >
                    {t('CSV')}
                    <img src={require('@/static/images/arrow_gray.svg')} css={dropDownIcon} />
                  </Button>
                  <Popup
                    isOpen={isDropDownOpen}
                    onClose={() => setIsDropDownOpen(false)}
                    customStyle={css({ width: 153, right: 0, top: 32 })}
                  >
                    <ul>
                      {tabType === TabType.Reservation && (
                        <li css={dropDownContentStyle} onClick={() => setIsCsvUploadModalOpen(true)}>
                          <p css={dropDownTitleStyle}>{t('Booking import')}</p>
                        </li>
                      )}
                      {isCsvUploadModalOpen && (
                        <CsvUploadModal
                          closeCsvUploadModal={() => setIsCsvUploadModalOpen(false)}
                          setIsModalOpen={setIsCsvUploadModalOpen}
                          fetchAdminCheckinList={() => fetchAdminCheckinList({ tabType, page, detailedResearch, sortKey })}
                        />
                      )}
                      {tabType === TabType.Reservation && hasTicketPlugin && (
                        <label>
                          <li css={dropDownContentStyle}>
                            <p css={dropDownTitleStyle}>{t('Ticket import')}</p>
                            <CSVReader
                              inputStyle={{ display: 'none', cursor: 'pointer' }}
                              parserOptions={{
                                header: false,
                                skipEmptyLines: true,
                              }}
                              onFileLoaded={(data, file) => {
                                onFileLoad(data, file)
                              }}
                            />
                          </li>
                        </label>
                      )}
                      <li css={dropDownContentStyle} onClick={downloadAccommodationManagementCsv}>
                        <p css={dropDownTitleStyle}>{t('Booking export')}</p>
                      </li>
                      <li css={dropDownContentStyle} onClick={downloadCustomExport}>
                        <p css={dropDownTitleStyle}>{t('Custom export')}</p>
                      </li>
                      <li css={dropDownContentStyle} onClick={() => history.push({ pathname: 'csv-upload-history/list' })}>
                        <p css={dropDownTitleStyle}>{t('Import history')}</p>
                      </li>
                      <li css={dropDownContentStyle} onClick={() => history.push({ pathname: 'csv-download-history/list' })}>
                        <p css={dropDownTitleStyle}>{t('Csv download history')}</p>
                      </li>
                    </ul>
                  </Popup>
                </div>
              )}
            </div>
          </div>
          <div css={listContainerGrayStyle}>
            {tabType === TabType.Reservation && (
              <ReservationList
                reservationList={reservationList}
                page={page}
                setPage={p => fetchTabList(p)}
                setIsLoading={setIsLoading}
                hasList={hasList}
                listTotal={listTotal}
                limit={perPageNumber}
                pagingIsDisabled={pagingIsDisabled}
                isStatusApproved={isStatusApproved}
                setIsStatusApproved={setIsStatusApproved}
                onClickReserch={refetchCurrentTab}
                onClickClearReserch={onClearSearchParams}
                detailedReserch={detailedResearch}
                setDetailedReserch={setDetailedResearch}
                sortKey={sortKey}
                listScrollRef={listScrollRef}
                onChangeSortKey={_sortKey => {
                  setSortKey(_sortKey)
                  fetchTabList(1, _sortKey)
                }}
              />
            )}
            {tabType === 'checkin' && (
              <CheckinList
                checkinList={checkinList}
                page={page}
                setPage={p => fetchTabList(p)}
                hasList={hasList}
                listTotal={listTotal}
                limit={perPageNumber}
                pagingIsDisabled={pagingIsDisabled}
                isStatusApproved={isStatusApproved}
                setIsStatusApproved={setIsStatusApproved}
                onClickReserch={refetchCurrentTab}
                onClickClearReserch={onClearSearchParams}
                detailedReserch={detailedResearch}
                setDetailedReserch={setDetailedResearch}
                setIsLoading={setIsLoading}
                sortKey={sortKey}
                listScrollRef={listScrollRef}
                onChangeSortKey={_sortKey => {
                  setSortKey(_sortKey)
                  fetchTabList(1, _sortKey)
                }}
              />
            )}
            {tabType === 'stay' && (
              <StayList
                reservationList={reservationList}
                page={page}
                setPage={p => fetchTabList(p)}
                setIsLoading={setIsLoading}
                hasList={hasList}
                listTotal={listTotal}
                limit={perPageNumber}
                pagingIsDisabled={pagingIsDisabled}
                isStatusApproved={isStatusApproved}
                setIsStatusApproved={setIsStatusApproved}
                onClickReserch={refetchCurrentTab}
                onClickClearReserch={onClearSearchParams}
                detailedReserch={detailedResearch}
                setDetailedReserch={setDetailedResearch}
                sortKey={sortKey}
                listScrollRef={listScrollRef}
                onChangeSortKey={_sortKey => {
                  setSortKey(_sortKey)
                  fetchTabList(1, _sortKey)
                }}
              />
            )}
            {tabType === 'checkout' && (
              <CheckoutList
                reservationList={reservationList}
                page={page}
                setPage={p => fetchTabList(p)}
                listTotal={listTotal}
                limit={perPageNumber}
                pagingIsDisabled={pagingIsDisabled}
                onClickReserch={refetchCurrentTab}
                onClickClearReserch={onClearSearchParams}
                detailedReserch={detailedResearch}
                setDetailedReserch={setDetailedResearch}
                sortKey={sortKey}
                listScrollRef={listScrollRef}
                onChangeSortKey={_sortKey => {
                  setSortKey(_sortKey)
                  fetchTabList(1, _sortKey)
                }}
              />
            )}
            {/* URL を直接書き換えても、タブを表示しない施設ではタスクの API を呼ばない */}
            {tabType === TabType.ReservationTask && hasReservationTask && (
              <ReservationTaskTab key={reservationTaskTabKey} onTasksChanged={fetchUnhandledTaskTotal} />
            )}
          </div>
        </div>
      </div>
      <LoadingFull isLoading={isLoading} />
    </div>
  )
}

const tabStyle = css({
  display: 'flex',
  justifyContent: 'left',
  fontSize: 14,
  fontWeight: 'bold',
  padding: 6,
  paddingBottom: 0,
  marginLeft: -6,
  overflow: 'hidden',
  position: 'relative',
  zIndex: 1,
})

const nonActiveTabStyle = css({
  cursor: 'pointer',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: 160,
  height: 45,
  color: '#676767',
  backgroundColor: '#f5f5f5',
  borderRadius: '5px 5px 0 0',
  boxShadow: '0px 0px 6px #0000001A',
})

const activeTabStyle = css(nonActiveTabStyle, {
  color: '#F2A40B',
  backgroundColor: '#FFFFFF',
  borderRadius: '5px 5px 0 0',
  boxShadow: '0px 0px 6px #0000001A',
})

const tabCountTextStyle = css({
  minWidth: 102,
  marginRight: 8,
  textAlign: 'center',
})

const reservationTaskTabTextStyle = css({
  marginRight: 8,
})

const headerBarRightWrapperStyle = css({
  marginLeft: 'auto',
  display: 'flex',
  zIndex: 10,
})

const statusChangeButtonStyle = css({
  color: '#F2A40B',
})

const dropDownButtonStyle = css({
  position: 'relative',
})

const dropDownIcon = css({
  marginLeft: 8,
})

const dropDownContentStyle = css({
  display: 'flex',
  height: 34,
  listStyle: 'none',
  alignItems: 'center',
  padding: '0 16px',
  cursor: 'pointer',
  '&:hover': {
    background: '#f2f2f2',
  },
})

const dropDownTitleStyle = css({
  fontSize: 14,
  color: '#272727',
})
